import React, { useState, useMemo, useEffect } from "react";
import { DatePicker, Select, message, Row, Col } from "antd";
import dayjs from "dayjs";
import StatCard from "../../../components/reusable/StatCard";
import LoadingSpinner from "src/components/reusable/LoadingSpinner";
import StyledTable from "../../../components/reusable/StyledTable";
import {
  getPipelineHealthData,
  getTenants,
} from "../../../services/AdminPortalAPIs";
import { pipelineColumns, PipelineData, RawPipeline } from "./PipelineColumns"; 
import StyledDropdown from "src/components/reusable/StyledDropdown";


const { RangePicker } = DatePicker;
const { Option } = Select;


const PipelineDashboard: React.FC = () => {
  const [tenants, setTenants] = useState<string[]>([]);
  const [selectedClient, setSelectedClient] = useState<string>("Hinz");
    const [lastRefreshed, setLastRefreshed] = useState("");
     const tenantsRef = React.useRef(false);
const [selectedStatus, setSelectedStatus] = useState<string[]>([
  "SUCCESS",
  "FAILED",

]);

  const [pipelineData, setPipelineData] = useState<PipelineData[]>([]);
  const [statusCounts, setStatusCounts] = useState({
    scheduled: 0,
    success: 0,
    failed: 0,
    total: 0,
  });

  const today = dayjs();
  const threeDaysAgo = today.subtract(2, "day");

  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    threeDaysAgo,
    today,
  ]);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const now = dayjs().format("hh:mm:ss A, DD-MM-YYYY [PKT]");
    setLastRefreshed(now);
  }, []);

const fetchInitialData = async () => {

  try {
    const tenantsResponse = await getTenants();

    const tenantsRaw = tenantsResponse.data.data.map((t: any) =>
      t.tenant_name?.trim().toLowerCase()
    );

    const uniqueClients = Array.from(new Set(tenantsRaw)) as string[];
    const titleCasedClients = uniqueClients
      .map(name => name.charAt(0).toUpperCase() + name.slice(1))
      .sort();

    setTenants(titleCasedClients);

    if (titleCasedClients.includes("Hinz")) {
      setSelectedClient("Hinz"); // ✅ triggers useEffect
    } else if (titleCasedClients.length > 0) {
      setSelectedClient(titleCasedClients[0]); // ✅ triggers useEffect
    }
  } catch (err) {
    console.error("Tenant fetch error", err);
    message.error("Failed to fetch tenant list");
  }
};


  const fetchPipelineData = async (
  client: string,
  startDate: dayjs.Dayjs,
  endDate: dayjs.Dayjs
) => {
  setLoading(true);
  try {
    const payload = {
  client_name: client.toLowerCase().replace(/\s+/g, "_"),

      start_date: startDate.format("YYYY-MM-DD"),
      end_date: endDate.format("YYYY-MM-DD"),
    };

    const res = await getPipelineHealthData(payload);

    // Defensive check
    if (!res?.data?.pipelines || !Array.isArray(res.data.pipelines)) {
      throw new Error("Invalid pipelines data");
    }
 const formatExecutionTime = (seconds: number): string => {
  if (isNaN(seconds) || seconds <= 0) {return "0m 0s";}

  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60); // 🔁 Rounded seconds

  return `${mins}m ${secs}s`;
};

 const formattedData = (res.data.pipelines as RawPipeline[]).map((p, idx) => {
  const dt = dayjs(p["Start DateTime"]);
  return {
    key: `${idx}-${p["Pipeline Name"]}`,
    name: p["Pipeline Name"],
    client: p["Client Name"],
    source: p["Sources"],
    startDate: dt.format("DD/MM/YYYY"),
    startTime: dt.format("HH:mm:ss"),
    status: p["Current Status"],
    executionTime: formatExecutionTime(Number(p["Execution Time"])),
    completion: (() => {
      const raw = String(p["Completion"]).replace("%", "").trim();
      const num = Number(raw);
      return isNaN(num) ? "0%" : `${Math.round(num)}%`;
    })()
  };
});

    setPipelineData(formattedData);
    setStatusCounts(res.data.status_counts || {});
  } catch (error) {
    console.error("Pipeline data fetch error:", error);
    message.error("Failed to fetch pipeline data.");
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
     if (tenantsRef.current) {return;}
 tenantsRef.current = true;
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (selectedClient) {
      fetchPipelineData(selectedClient, dateRange[0], dateRange[1]);
    }
  }, [selectedClient, dateRange]);

const filteredData = useMemo(() => {
  return pipelineData.filter(item => {
    const normalizedStatus = item.status.toUpperCase();
 const statusMatch =
  selectedStatus.length === 0 || selectedStatus.includes(normalizedStatus);
    return statusMatch;
  });
}, [pipelineData, selectedStatus]);

  return (
    <div className="admin-portal-page">
      <div className="header">
        <div className="heading-primary">Pipeline Monitoring Dashboard</div>
        <div className="last-refreshed">
     Last refreshed at {lastRefreshed}
        </div>
      </div>

      <div
        className="filters"
      >
 <StyledDropdown
  mode="multiple"
  value={selectedStatus}
  placeholder="Status"
  onChange={setSelectedStatus}
  className="status-select"
  aria-label="Status Filter"
  maxTagCount="responsive"
  options={[
    { value: "SUCCESS", label: "Success" },
    { value: "FAILED", label: "Failed" },
  ]}
/>
      <RangePicker
  value={dateRange}
  onChange={dates => {
    if (dates && dates[0] && dates[1]) {
      setDateRange([dates[0], dates[1]]);
    }
  }}
  format="DD/MM/YYYY"
  className="date-picker"
  aria-label="Date Range Filter"
/>
<StyledDropdown
  value={selectedClient}
  onChange={setSelectedClient}
  className="client-select"
  style={{ width: 200 }}
  aria-label="Client Filter"
  options={tenants.map(client => ({
    value: client,
    label: client,
  }))}
/>
      </div>
{loading ? (
      <LoadingSpinner />
    ) : (
      <Row gutter={16} className="field-labels">
        
        <Col span={8}>
          <StatCard
            label="Scheduled Pipelines"
            value={statusCounts.scheduled}
          />

        </Col>
        <Col span={8}>
          <StatCard
            label="Successful Pipelines"
            value={statusCounts.success}
          />
        </Col>
        <Col span={8}>
          <StatCard label="Failed Pipelines" value={statusCounts.failed} />
        </Col>
      </Row>
    )}
  {filteredData.length === 0 ? (
  <div style={{ textAlign: "center", margin: "40px 0", color: "#888" }}>
    <p>No pipelines found for the selected filters.</p>
  </div>
) : (
  <StyledTable
    columns={pipelineColumns}
    loading={{
      spinning: loading,
      indicator: <LoadingSpinner />,
    }}
    dataSource={filteredData}
    pagination={{
      pageSize: 3,
      showSizeChanger: false,
    }}
    className="pipeline-table"
    rowKey="key"
    rowClassName={() => "textCaption"}
  />
)}
    </div>
  );
};

export default PipelineDashboard;
